/**
 * Structured JSON error responses for every `/api/*` route.
 *
 * Agents (and humans debugging with curl) can't do anything useful with a bare
 * `{ "error": "Not found" }` string, let alone the HTML 404 page Next.js serves
 * for unmatched `/api/*` paths. Every error body here carries:
 *
 *   - `error.code`    a stable, machine-readable slug (never localized)
 *   - `error.message` a one-line human summary
 *   - `error.hint`    what the caller should actually do next
 *   - `error.status`  mirrors the HTTP status, for clients that lose it
 *   - `error.docs`    link to the OpenAPI spec describing this surface
 *
 * The shape is deliberately nested under `error` so a success body can never be
 * mistaken for a failure: callers test `"error" in body`, not a status heuristic.
 */

/** Canonical error codes. Add new ones here so the set stays enumerable. */
export const API_ERROR_CODES = [
  'bad_request',
  'invalid_parameter',
  'missing_parameter',
  'unauthorized',
  'forbidden',
  'not_found',
  'route_not_found',
  'method_not_allowed',
  'conflict',
  'rate_limited',
  'upstream_error',
  'internal_error',
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

/** Where agents can read the machine-readable description of this API. */
export const API_DOCS_URL = 'https://gamerplug.app/openapi.json'

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode
    message: string
    hint: string
    status: number
    docs: string
    /** Present only when the failure is tied to a specific input field. */
    parameter?: string
  }
}

/** Default HTTP status for each code, so callers only pass one when it differs. */
const DEFAULT_STATUS: Record<ApiErrorCode, number> = {
  bad_request: 400,
  invalid_parameter: 400,
  missing_parameter: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  route_not_found: 404,
  method_not_allowed: 405,
  conflict: 409,
  rate_limited: 429,
  upstream_error: 502,
  internal_error: 500,
}

/** Fallback guidance per code, used when a call site has nothing better to say. */
const DEFAULT_HINT: Record<ApiErrorCode, string> = {
  bad_request:
    'Check the request body and query parameters against the OpenAPI spec at /openapi.json.',
  invalid_parameter:
    'Correct the parameter value and retry. The accepted format is described in /openapi.json.',
  missing_parameter:
    'Add the required parameter and retry. Required parameters are listed in /openapi.json.',
  unauthorized: 'Supply valid credentials and retry.',
  forbidden: 'This endpoint is not available to the current caller.',
  not_found:
    'Verify the identifier is correct. It may have been removed, renamed, or never existed.',
  route_not_found:
    'This path is not part of the GamerPlug API. See /openapi.json for the full list of endpoints, or /llms.txt for a site overview.',
  method_not_allowed:
    'Use one of the HTTP methods listed for this path in /openapi.json.',
  conflict: 'The resource already exists or is in a state that blocks this operation.',
  rate_limited: 'Wait before retrying. Reduce request frequency to stay within the limit.',
  upstream_error:
    'A third-party service GamerPlug depends on failed. Retry after a short backoff.',
  internal_error: 'Retry after a short backoff. If it persists, contact support@gamerplug.app.',
}

export interface ApiErrorOptions {
  /** Override the default status for the code. */
  status?: number
  /** Override the default hint with something specific to the call site. */
  hint?: string
  /** Name of the offending query/body field, when there is one. */
  parameter?: string
}

/**
 * Build the JSON body for an API error. Pure — returns a plain object so it can
 * be unit-tested without a Response, and serialized by whichever runtime helper
 * the route already uses.
 */
export function apiErrorBody(
  code: ApiErrorCode,
  message: string,
  options: ApiErrorOptions = {}
): ApiErrorBody {
  const status = options.status ?? DEFAULT_STATUS[code]
  return {
    error: {
      code,
      message,
      hint: options.hint ?? DEFAULT_HINT[code],
      status,
      docs: API_DOCS_URL,
      ...(options.parameter ? { parameter: options.parameter } : {}),
    },
  }
}

/** The HTTP status that `apiErrorBody` would report for `code`/`options`. */
export function apiErrorStatus(code: ApiErrorCode, options: ApiErrorOptions = {}): number {
  return options.status ?? DEFAULT_STATUS[code]
}

/**
 * A `Response` carrying the structured error. Used by routes and by the
 * `/api/*` catch-all. `Response` (not `NextResponse`) keeps this importable
 * from any runtime, including the edge middleware.
 */
export function apiErrorResponse(
  code: ApiErrorCode,
  message: string,
  options: ApiErrorOptions = {}
): Response {
  const body = apiErrorBody(code, message, options)
  return new Response(JSON.stringify(body, null, 2), {
    status: body.error.status,
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      // Errors are caller-specific; never let a CDN pin one to a URL.
      'Cache-Control': 'no-store',
    },
  })
}

/**
 * The human message from a parsed `/api/*` response body, for client code.
 * `error` used to be a bare string and is now an object; rendering the object
 * straight into JSX throws React error #31, so always go through this.
 */
export function apiErrorMessage(body: unknown, fallback: string): string {
  const error = (body as { error?: unknown } | null | undefined)?.error
  if (typeof error === 'string' && error) return error
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message) return message
  }
  return fallback
}
